/**
 * Request throttler to prevent too many API calls
 */
class RequestThrottle {
  private lastRequestTime: Map<string, number> = new Map();
  private readonly throttleDuration: number;

  constructor(throttleDuration: number = 500) {
    this.throttleDuration = throttleDuration;
  }

  /**
   * Check if a request should proceed based on throttle
   * @param url - The request URL
   * @returns true if request should proceed, false if throttled
   */
  shouldProceed(url: string): boolean {
    const now = Date.now();
    const lastTime = this.lastRequestTime.get(url);

    if (lastTime === undefined) {
      this.lastRequestTime.set(url, now);
      return true;
    }

    const timeSinceLastRequest = now - lastTime;
    if (timeSinceLastRequest >= this.throttleDuration) {
      this.lastRequestTime.set(url, now);
      return true;
    }

    return false;
  }

  /**
   * Clear throttle for a specific URL
   */
  clear(url: string): void {
    this.lastRequestTime.delete(url);
  }

  /**
   * Clear all throttles
   */
  clearAll(): void {
    this.lastRequestTime.clear();
  }
}

// Global request throttle instance
// Reduced throttle duration from 500ms to 200ms for better responsiveness
// Backend has proper rate limiting, so we can be less aggressive here
export const requestThrottle = new RequestThrottle(200);

/**
 * Helper function to delay execution
 */
export const delay = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

